import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle, BarChart3, BookOpen, CheckCircle2, ChevronDown, Clock, Crown, Database, Edit3, Plus, RefreshCcw, RotateCcw, Search, Settings, Shield, Trash2, Users } from 'lucide-react';
import { adminService, getApiErrorMessage } from '../services/api';
import { getUser } from '../services/auth';
import './AdminDashboard.css';

const TABS = [
  { id: 'overview', label: 'Overview', Icon: BarChart3 },
  { id: 'users', label: 'Users & Limits', Icon: Users },
  { id: 'lexicons', label: 'Lexicons', Icon: BookOpen },
  { id: 'jobs', label: 'Generation Jobs', Icon: Activity },
];

const numberFmt = new Intl.NumberFormat();

function canOpenAdmin(user) {
  return Boolean(user?.permissions?.canManageBasePrompt || user?.permissions?.canManageResourcePrompts);
}

function dateText(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString();
}

function resetTimeText(limits) {
  const label = limits?.resetTimeLabel || '00:00 UTC';
  return `Every day at ${label}`;
}

function shortDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function defaultTokenWindow() {
  const end = new Date();
  const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
}

function tokenWindowText(window) {
  const fallback = defaultTokenWindow();
  const start = shortDate(window?.start) || shortDate(fallback.start);
  const end = shortDate(new Date());
  return `${start} - ${end}`;
}

function dateTimeInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function PageState({ loading, error, empty, children }) {
  if (loading) return <div className="admin-state"><RefreshCcw size={18} /> Loading admin data...</div>;
  if (error) return <div className="admin-state error"><AlertTriangle size={18} /> {error}</div>;
  if (empty) return <div className="admin-state">No records found.</div>;
  return children;
}

function Pager({ page, limit, total, onPage }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="admin-pager">
      <button className="admin-btn" onClick={() => onPage(page - 1)} disabled={page <= 1}>Previous</button>
      <span>Page {page} of {totalPages}</span>
      <button className="admin-btn" onClick={() => onPage(page + 1)} disabled={page >= totalPages}>Next</button>
    </div>
  );
}

function TokenUsageCard({ stats, onSaveWindow, saving }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const total = Number(stats?.totalTokenUsage || 0);
  const usageWindow = stats?.tokenUsageWindow || defaultTokenWindow();
  const [draft, setDraft] = useState({
    start: dateTimeInputValue(usageWindow.start),
    end: dateTimeInputValue(new Date())
  });
  useEffect(() => {
    setDraft({
      start: dateTimeInputValue(usageWindow.start),
      end: dateTimeInputValue(new Date())
    });
  }, [usageWindow.start, stats?.totalTokenUsage]);
  return (
    <div className={`admin-stat-card admin-token-card ${expanded ? 'expanded' : ''}`}>
      <button type="button" className="admin-token-card-head" onClick={() => setExpanded(value => !value)}>
        <span><Database size={17} /></span>
        <div>
          <small>OpenAI tokens used</small>
          <strong>{numberFmt.format(total)}</strong>
        </div>
        <ChevronDown size={16} className="admin-token-chevron" />
      </button>
      {expanded && (
        <div className="admin-token-breakdown">
          <div className="admin-token-row single">
            <b>{usageWindow.isLive ? 'Live time span' : 'Time span'}</b>
            <em>{tokenWindowText(usageWindow)}</em>
            <button
              type="button"
              className="admin-icon-btn admin-token-settings-btn"
              onClick={() => setEditing(true)}
              title="Configure token usage time span"
            >
              <Settings size={14} />
            </button>
          </div>
          {editing && (
            <div className="admin-token-window-editor">
              <div className="admin-token-date-fields">
                <label>
                  <span>Start date/time</span>
                  <input type="datetime-local" value={draft.start} onChange={(event) => setDraft({ ...draft, start: event.target.value })} />
                </label>
                <label>
                  <span>End date/time</span>
                  <input type="datetime-local" value={draft.end} onChange={(event) => setDraft({ ...draft, end: event.target.value })} />
                </label>
              </div>
              <div className="admin-token-window-actions">
                <button className="admin-btn" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                <button className="admin-btn primary" onClick={() => onSaveWindow(draft)} disabled={saving || !draft.start || !draft.end}>
                  {saving ? 'Saving...' : 'Apply'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PermissionControlBar({ limits, draft, setDraft, saving, onSave }) {
  const roleOptions = [...new Set([...(limits?.roleOptions || ['content_creator', 'user', 'community_admin']), ...(draft.limitedRoles || [])])];
  const selectedRoles = draft.limitedRoles || [];
  const roleLimits = draft.roleLimits || {};
  const toggleRole = (role) => {
    setDraft({
      ...draft,
      limitedRoles: selectedRoles.includes(role)
        ? selectedRoles.filter((item) => item !== role)
        : [...selectedRoles, role]
    });
  };
  const roleRows = selectedRoles.map((role) => (
    (limits?.roleRows || []).find((row) => row.role === role) || {
      role,
      description: 'Custom limited role.',
      users: 0,
      status: 'active'
    }
  )).map((row) => ({
    ...row,
    dailyLimit: Number(roleLimits[row.role] || draft.dailyLimit || row.dailyLimit || 100)
  }));
  const dailyLimit = Number(draft.dailyLimit || 100);
  const updateRoleLimit = (role) => {
    const nextLimit = Number(window.prompt(`Daily limit for ${role}`, roleLimits[role] || draft.dailyLimit || 100));
    if (!Number.isFinite(nextLimit) || nextLimit < 1) return;
    setDraft({
      ...draft,
      roleLimits: { ...roleLimits, [role]: Math.floor(nextLimit) }
    });
  };
  const removeRole = (role) => {
    const nextLimits = { ...roleLimits };
    delete nextLimits[role];
    setDraft({
      ...draft,
      limitedRoles: selectedRoles.filter((item) => item !== role),
      roleLimits: nextLimits
    });
  };
  const addCustomRole = () => {
    const role = String(window.prompt('Custom role key, for example content_reviewer') || '')
      .trim()
      .replace(/[\s-]+/g, '_')
      .toLowerCase();
    if (!role || selectedRoles.includes(role)) return;
    setDraft({
      ...draft,
      limitedRoles: [...selectedRoles, role],
      roleLimits: { ...roleLimits, [role]: dailyLimit }
    });
  };
  return (
    <section className="admin-panel admin-permission-panel">
      <div className="admin-panel-title-row">
        <div>
          <h3>Permission Control</h3>
          <p>Manage non-admin role permissions and daily generation limits. Limits reset every day at 00:00 UTC for all users.</p>
        </div>
        <button className="admin-btn primary" onClick={addCustomRole}><Plus size={15} /> Add Custom Role</button>
      </div>
      <div className="admin-permission-shell">
        <aside className="admin-limit-card">
          <label>
            Daily Generation Limit
            <input
              type="number"
              min="1"
              max="100000"
              value={draft.dailyLimit || ''}
              onChange={(event) => setDraft({ ...draft, dailyLimit: Number(event.target.value) })}
            />
          </label>
          <small>Default daily cap for selected limited roles. Role-specific limits can be edited in the table.</small>
          <div className="admin-admin-note"><Crown size={15} /> Admins have unlimited access.</div>
        </aside>
        <div className="admin-role-control">
          <span className="admin-field-label">Limited Roles</span>
          <div className="admin-role-toggle-row">
            {roleOptions.map((role) => (
              <button type="button" key={role} className={`admin-role-toggle ${selectedRoles.includes(role) ? 'active' : ''}`} onClick={() => toggleRole(role)}>
                <Users size={13} /> {role}
              </button>
            ))}
          </div>
          <div className="admin-table-wrap admin-role-table-wrap">
            <table className="admin-table admin-role-table">
              <thead><tr><th>Role</th><th>Description</th><th>Daily Limit</th><th>Users</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {roleRows.map((row) => (
                  <tr key={row.role}>
                    <td><span className="admin-role-pill">{row.role}</span></td>
                    <td>{row.description}</td>
                    <td>{numberFmt.format(row.dailyLimit)}</td>
                    <td>{numberFmt.format(row.users || 0)}</td>
                    <td><span className="admin-chip ok">Active</span></td>
                    <td>
                      <div className="admin-row-actions">
                        <button className="admin-icon-btn" onClick={() => updateRoleLimit(row.role)} title="Edit daily limit"><Edit3 size={14} /></button>
                        <button className="admin-icon-btn danger" onClick={() => removeRole(row.role)} title="Remove role"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="admin-limit-summary">
        <span>Admins unlimited: {(limits?.adminRoles || []).join(', ') || '-'}</span>
        <span>Limit resets: {resetTimeText(limits)}</span>
        <button className="admin-btn primary" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save controls'}</button>
      </div>
    </section>
  );
}

function Overview({ data, loading, error, limitDraft, setLimitDraft, savingLimits, onSaveLimits, savingTokenWindow, onSaveTokenWindow }) {
  const stats = data?.stats || {};
  const limits = data?.limits || {};
  const cards = [
    ['Total users', stats.totalUsers, Users],
    ['Active users', stats.activeUsers, CheckCircle2],
    ['Total lexicons', stats.totalLexicons, BookOpen],
    ['Lexicons Saved today', stats.lexiconsCreatedToday, Clock],
    ['Generated today', stats.generatedLexiconsToday, Activity],
    ['Published to Encye', stats.publishedToEncye, Database],
    ['Failed jobs', stats.failedGenerationJobs, AlertTriangle],
  ];
  const enrichedLimits = { ...limits, generatedToday: stats.generatedLexiconsToday };
  return (
    <PageState loading={loading} error={error}>
      <div className="admin-stat-grid">
        {cards.map(([label, value, Icon]) => (
          <div className="admin-stat-card" key={label}>
            <span><Icon size={17} /></span>
            <small>{label}</small>
            <strong>{numberFmt.format(Number(value || 0))}</strong>
          </div>
        ))}
        <TokenUsageCard stats={stats} saving={savingTokenWindow} onSaveWindow={onSaveTokenWindow} />
      </div>
      <PermissionControlBar
        limits={enrichedLimits}
        draft={limitDraft}
        setDraft={setLimitDraft}
        saving={savingLimits}
        onSave={onSaveLimits}
      />
    </PageState>
  );
}

function UsersTable({ rows, loading, error, q, setQ }) {
  return (
    <>
      <div className="admin-toolbar">
        <label className="admin-search"><Search size={15} /><input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Search users or roles" /></label>
      </div>
      <PageState loading={loading} error={error} empty={!rows.length}>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>User</th><th>Role</th><th>Status</th><th>Today</th><th>Limit</th><th>Remaining</th></tr></thead>
            <tbody>
              {rows.map((user) => (
                <tr key={user.id}>
                  <td><strong>{user.name}</strong><small>{user.email}</small></td>
                  <td>{(user.roles || []).join(', ') || user.role}</td>
                  <td><span className={`admin-chip ${user.isActive ? 'ok' : 'warn'}`}>{user.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td>{numberFmt.format(user.usedToday || 0)}</td>
                  <td>{user.limited ? user.limit : 'Unlimited'}</td>
                  <td>{user.limited ? user.remaining : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PageState>
    </>
  );
}

function LexiconsTable({ data, loading, error, filters, setFilters }) {
  const items = data?.items || [];
  const page = data?.page || filters.page || 1;
  const limit = data?.limit || filters.limit || 25;
  const total = data?.total || 0;
  return (
    <>
      <div className="admin-toolbar">
        <label className="admin-search"><Search size={15} /><input value={filters.q} onChange={(event) => setFilters({ ...filters, q: event.target.value, page: 1 })} placeholder="Search lexicons" /></label>
        <select value={filters.published} onChange={(event) => setFilters({ ...filters, published: event.target.value, page: 1 })}>
          <option value="">All publish states</option>
          <option value="true">Published to Encye</option>
          <option value="false">Not published</option>
        </select>
      </div>
      <PageState loading={loading} error={error} empty={!items.length}>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Title</th><th>Creator</th><th>Source</th><th>Variant group</th><th>Encye</th><th>Created</th></tr></thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.title}</strong></td>
                  <td>{item.creator ? <><strong>{item.creator.name}</strong><small>{item.creator.email}</small></> : '-'}</td>
                  <td>{item.sourceType || '-'}</td>
                  <td>{item.variantGroupKey || '-'}</td>
                  <td><span className={`admin-chip ${item.encyeLexiconId ? 'ok' : item.encyePublicationError ? 'warn' : ''}`}>{item.encyeLexiconId ? 'Published' : item.encyePublicationError ? 'Failed' : 'Pending'}</span></td>
                  <td>{dateText(item.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager page={page} limit={limit} total={total} onPage={(nextPage) => setFilters({ ...filters, page: nextPage })} />
      </PageState>
    </>
  );
}

function JobsTable({ data, loading, error, filters, setFilters, restartingJobId, onRestartFailed }) {
  const items = data?.items || [];
  const page = data?.page || filters.page || 1;
  const limit = data?.limit || filters.limit || 25;
  const total = data?.total || 0;
  return (
    <>
      <div className="admin-toolbar">
        <select value={filters.status || ''} onChange={(event) => setFilters({ ...filters, status: event.target.value, page: 1 })}>
          <option value="">All jobs</option>
          <option value="failed">Failed jobs</option>
          <option value="running">Running</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      <PageState loading={loading} error={error} empty={!items.length}>
        <div className="admin-table-wrap">
          <table className="admin-table admin-jobs-table">
            <thead><tr><th>Created</th><th>Creator</th><th>Status</th><th>Total</th><th>Done / Failed</th><th>Running / Pending</th><th>Tokens</th><th>Concurrency</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map((job) => {
                const canRestart = Number(job.failed || 0) > 0 && ['failed', 'completed_with_errors'].includes(job.status);
                const restarting = restartingJobId === job.id;
                return (
                  <tr key={job.id}>
                    <td>{dateText(job.createdAt)}</td>
                    <td>{job.creator ? <><strong>{job.creator.name}</strong><small>{job.creator.email}</small></> : '-'}</td>
                    <td><span className={`admin-chip ${job.status === 'completed' ? 'ok' : job.status?.includes('error') || job.status === 'failed' ? 'warn' : ''}`}>{job.status}</span></td>
                    <td>{job.total}</td>
                    <td>{job.completed} / {job.failed}</td>
                    <td>{job.running} / {job.pending}</td>
                    <td>{numberFmt.format(job.usage?.total_tokens || 0)}</td>
                    <td>{job.maxConcurrency}</td>
                    <td>
                      <button
                        className="admin-btn admin-restart-btn"
                        onClick={() => onRestartFailed(job.id)}
                        disabled={!canRestart || restarting}
                        title={canRestart ? 'Restart failed items in this job' : 'No failed items to restart'}
                      >
                        <RotateCcw size={14} /> {restarting ? 'Restarting...' : 'Restart'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pager page={page} limit={limit} total={total} onPage={(nextPage) => setFilters({ ...filters, page: nextPage })} />
      </PageState>
    </>
  );
}

function AdminDashboard() {
  const user = useMemo(getUser, []);
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [lexicons, setLexicons] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});
  const [savingLimits, setSavingLimits] = useState(false);
  const [savingTokenWindow, setSavingTokenWindow] = useState(false);
  const [restartingJobId, setRestartingJobId] = useState('');
  const [limitDraft, setLimitDraft] = useState({ dailyLimit: 100, limitedRoles: ['content_creator', 'user'], roleLimits: {} });
  const [userQ, setUserQ] = useState('');
  const [lexiconFilters, setLexiconFilters] = useState({ q: '', published: '', page: 1, limit: 25 });
  const [jobFilters, setJobFilters] = useState({ page: 1, limit: 25, status: 'failed' });

  const load = useCallback(async (key, fn) => {
    setLoading((prev) => ({ ...prev, [key]: true }));
    setError((prev) => ({ ...prev, [key]: '' }));
    try {
      await fn();
    } catch (err) {
      setError((prev) => ({ ...prev, [key]: getApiErrorMessage(err, 'Could not load admin data.') }));
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  useEffect(() => {
    if (!canOpenAdmin(user)) return;
    load('overview', async () => setOverview((await adminService.overview()).data));
  }, [load, user]);

  useEffect(() => {
    if (tab !== 'overview' || !canOpenAdmin(user) || !overview?.stats?.tokenUsageWindow?.isLive) return;
    const id = window.setInterval(async () => {
      try {
        setOverview((await adminService.overview()).data);
      } catch (err) {
        setError((prev) => ({ ...prev, overview: getApiErrorMessage(err, 'Could not refresh token usage.') }));
      }
    }, 30000);
    return () => window.clearInterval(id);
  }, [tab, user, overview?.stats?.tokenUsageWindow?.isLive]);

  useEffect(() => {
    if (!overview?.limits) return;
    setLimitDraft({
      dailyLimit: overview.limits.dailyLimit || 100,
      limitedRoles: overview.limits.limitedRoles || ['content_creator', 'user'],
      roleLimits: overview.limits.roleLimits || {}
    });
  }, [overview]);

  const saveLimits = async () => {
    setSavingLimits(true);
    setError((prev) => ({ ...prev, overview: '' }));
    try {
      const response = await adminService.updateLimits(limitDraft);
      setOverview((prev) => ({
        ...(prev || {}),
        limits: response.data
      }));
      if (tab === 'users') {
        const usersResponse = await adminService.users({ q: userQ });
        setUsers(usersResponse.data.users || []);
      }
    } catch (err) {
      setError((prev) => ({ ...prev, overview: getApiErrorMessage(err, 'Could not save limit controls.') }));
    } finally {
      setSavingLimits(false);
    }
  };

  const saveTokenWindow = async ({ start, end }) => {
    setSavingTokenWindow(true);
    setError((prev) => ({ ...prev, overview: '' }));
    try {
      await adminService.updateTokenWindow({
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString()
      });
      const response = await adminService.overview();
      setOverview(response.data);
    } catch (err) {
      setError((prev) => ({ ...prev, overview: getApiErrorMessage(err, 'Could not save token usage time span.') }));
    } finally {
      setSavingTokenWindow(false);
    }
  };

  const restartFailedJob = async (jobId) => {
    setRestartingJobId(jobId);
    setError((prev) => ({ ...prev, jobs: '' }));
    try {
      await adminService.restartFailedGenerationJob(jobId);
      const response = await adminService.generationJobs(jobFilters);
      setJobs(response.data);
      const overviewResponse = await adminService.overview();
      setOverview(overviewResponse.data);
    } catch (err) {
      setError((prev) => ({ ...prev, jobs: getApiErrorMessage(err, 'Could not restart failed job.') }));
    } finally {
      setRestartingJobId('');
    }
  };

  useEffect(() => {
    if (tab !== 'users' || !canOpenAdmin(user)) return;
    const id = window.setTimeout(() => {
      load('users', async () => setUsers((await adminService.users({ q: userQ })).data.users || []));
    }, 250);
    return () => window.clearTimeout(id);
  }, [load, tab, user, userQ]);

  useEffect(() => {
    if (tab !== 'lexicons' || !canOpenAdmin(user)) return;
    const id = window.setTimeout(() => {
      load('lexicons', async () => setLexicons((await adminService.lexicons(lexiconFilters)).data));
    }, 250);
    return () => window.clearTimeout(id);
  }, [load, tab, user, lexiconFilters]);

  useEffect(() => {
    if (tab !== 'jobs' || !canOpenAdmin(user)) return;
    load('jobs', async () => setJobs((await adminService.generationJobs(jobFilters)).data));
  }, [load, tab, user, jobFilters]);

  if (!canOpenAdmin(user)) {
    return (
      <div className="admin-dashboard">
        <div className="admin-denied"><Shield size={28} /><h2>Admin access required</h2><p>You need organization admin permissions to view this page.</p></div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div><h2>Admin</h2><p>Organization activity, limits, users, lexicons, and generation usage.</p></div>
        <button className="admin-btn primary" onClick={() => {
          if (tab === 'overview') load('overview', async () => setOverview((await adminService.overview()).data));
          if (tab === 'users') load('users', async () => setUsers((await adminService.users({ q: userQ })).data.users || []));
          if (tab === 'lexicons') load('lexicons', async () => setLexicons((await adminService.lexicons(lexiconFilters)).data));
          if (tab === 'jobs') load('jobs', async () => setJobs((await adminService.generationJobs(jobFilters)).data));
        }}><RefreshCcw size={15} /> Refresh</button>
      </header>
      <nav className="admin-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}><Icon size={15} /> {label}</button>
        ))}
      </nav>
      {tab === 'overview' && (
        <Overview
          data={overview}
          loading={loading.overview}
          error={error.overview}
          limitDraft={limitDraft}
          setLimitDraft={setLimitDraft}
          savingLimits={savingLimits}
          onSaveLimits={saveLimits}
          savingTokenWindow={savingTokenWindow}
          onSaveTokenWindow={saveTokenWindow}
        />
      )}
      {tab === 'users' && <UsersTable rows={users} loading={loading.users} error={error.users} q={userQ} setQ={setUserQ} />}
      {tab === 'lexicons' && <LexiconsTable data={lexicons} loading={loading.lexicons} error={error.lexicons} filters={lexiconFilters} setFilters={setLexiconFilters} />}
      {tab === 'jobs' && (
        <JobsTable
          data={jobs}
          loading={loading.jobs}
          error={error.jobs}
          filters={jobFilters}
          setFilters={setJobFilters}
          restartingJobId={restartingJobId}
          onRestartFailed={restartFailedJob}
        />
      )}
    </div>
  );
}

export default AdminDashboard;
